import { useCallback } from 'react';
import { v4 as uuid4 } from 'uuid';

import { TrackingEvents, sendLoggingEvents } from '../../../events';
import { useGetTelehealthSessionInfoCallback } from '../../../hooks';
import { useAtomCallbackMemoized } from '../../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../../segment/segment';
import { EventTypeEnum } from '../../../segment/types';
import { trySafely } from '../../../utils';
import { providerShortIdAtom, visitorPresenceAtom } from '../../state';
import { admissionTrackingHistory } from './AdmissionTrackingHistory';

export function useTrackAdmissionClickCallback() {
  const getProviderShortId = useAtomCallbackMemoized((get) => get(providerShortIdAtom), []);
  const getVisitorPresence = useAtomCallbackMemoized((get) => get(visitorPresenceAtom), []);
  const { userClicked } = useTrackEvent();
  const getVideoPlatform = useVideoPlatformCallback();
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();

  return useCallback(
    ({ visitorId }: { visitorId: string }) => {
      const admissionTransactionId = uuid4();
      const providerShortId = getProviderShortId();
      const waitingVisitor = getVisitorPresence()[visitorId];
      const videoPlatform = getVideoPlatform();
      const patientShortId = waitingVisitor?.patientShortId;

      const dispatchTrackingEvents = async () => {
        const { data: telehealthSessionInfoData } =
          patientShortId || providerShortId
            ? ((await trySafely(
                getTelehealthSessionInfo({
                  variables: {
                    providerShortId,
                    patientShortId,
                  },
                }),
              )) ?? {})
            : {};
        const appointmentShortId = telehealthSessionInfoData?.telehealthSessionInfo?.appointment?.shortId;

        sendLoggingEvents(TrackingEvents.PROVIDER_ADMIT_CLIENT, {
          admissionTransactionId,
          joinRoomMode: waitingVisitor?.joinRoomMode ?? 'auto',
          patientShortId: waitingVisitor?.patientShortId,
          providerShortId: providerShortId,
          videoPlatform,
          visitorUuid: visitorId,
        });
        userClicked(EventTypeEnum.PROVIDER_ADMIT_CLIENT, {
          appointmentShortId: appointmentShortId ?? '',
          patientShortId,
          providerShortId: providerShortId ?? '',
          source: videoPlatform ?? '',
          txId: admissionTransactionId,
        });
      };

      void dispatchTrackingEvents();

      admissionTrackingHistory.addTransaction({
        visitorId,
        admissionTransactionId,
      });

      return admissionTransactionId;
    },
    [getProviderShortId, getTelehealthSessionInfo, getVideoPlatform, getVisitorPresence, userClicked],
  );
}
